/**
 * Nmbrs Sync History
 *
 * Paginated list of past sync operations with expandable error details.
 * Story Nmbrs-2.2: Sync Status Dashboard
 *
 * Design system colors:
 * - Navy #2F5A7A: headers, primary
 * - Teal #54949E: secondary info
 * - Green #B7D945: success
 * - Red #D64545: errors
 */

import { useState } from 'react';
import { useSyncLog, useSyncLogDetail } from '../hooks/useNmbrsSync';
import type { SyncLogEntry } from '../hooks/useNmbrsSync';

const SYNC_TYPE_LABELS: Record<string, string> = {
  scheduled: 'Gepland',
  manual: 'Handmatig',
  single: 'Enkel',
};

const STATUS_CONFIG: Record<string, { label: string; bgColor: string; textColor: string }> = {
  running: { label: 'Bezig...', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
  completed: { label: 'Voltooid', bgColor: 'bg-[#B7D945]/10', textColor: 'text-[#6B8C1A]' },
  failed: { label: 'Mislukt', bgColor: 'bg-red-50', textColor: 'text-[#D64545]' },
  partial: { label: 'Gedeeltelijk', bgColor: 'bg-amber-50', textColor: 'text-amber-800' },
};

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return 'Bezig...';
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const seconds = Math.round((end - start) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function SyncLogDetailPanel({ syncLogId }: { syncLogId: number }) {
  const { data, isLoading } = useSyncLogDetail(syncLogId);

  if (isLoading) {
    return (
      <div className="px-4 py-3 bg-gray-50">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2F5A7A]" />
          Details laden...
        </div>
      </div>
    );
  }

  const detail = data?.data;
  if (!detail) return null;

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-[#E2E8F0]">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-sm">
        <div>
          <span className="text-gray-500">Gesynchroniseerd:</span>{' '}
          <span className="font-medium text-[#1E293B]">{detail.syncedCount}</span>
        </div>
        <div>
          <span className="text-gray-500">Fouten:</span>{' '}
          <span className="font-medium text-[#1E293B]">{detail.errorCount}</span>
        </div>
        <div>
          <span className="text-gray-500">Overgeslagen:</span>{' '}
          <span className="font-medium text-[#1E293B]">{detail.skippedCount}</span>
        </div>
        <div>
          <span className="text-gray-500">Duur:</span>{' '}
          <span className="font-medium text-[#1E293B]">
            {formatDuration(detail.startedAt, detail.completedAt)}
          </span>
        </div>
      </div>

      {detail.errors.length > 0 && (
        <div className="mt-3">
          <h5 className="text-xs font-semibold text-[#D64545] uppercase mb-2">
            Fouten ({detail.errors.length})
          </h5>
          <div className="space-y-1">
            {detail.errors.map((err, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 p-2 bg-red-50 rounded text-sm"
              >
                <span className="text-[#D64545] font-mono text-xs shrink-0">
                  Medewerker #{err.employeeId}
                </span>
                <span className="text-gray-700">{err.error}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.errors.length === 0 && detail.status === 'completed' && (
        <p className="text-sm text-[#6B8C1A]">Alle medewerkers succesvol gesynchroniseerd.</p>
      )}
    </div>
  );
}

function SyncLogRow({
  entry,
  isExpanded,
  onToggle,
}: {
  entry: SyncLogEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const statusConfig = STATUS_CONFIG[entry.status] ?? {
    label: entry.status,
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
  };

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-3 text-sm text-[#1E293B]">
          <div className="flex items-center gap-2">
            <svg
              className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {formatDateTime(entry.startedAt)}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-[#54949E]">
          {SYNC_TYPE_LABELS[entry.syncType] ?? entry.syncType}
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}
          >
            {statusConfig.label}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-[#1E293B] text-right">
          {entry.syncedCount}/{entry.totalEmployees}
          {entry.errorCount > 0 && (
            <span className="text-[#D64545] ml-1">({entry.errorCount})</span>
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={4} className="p-0">
            <SyncLogDetailPanel syncLogId={entry.id} />
          </td>
        </tr>
      )}
    </>
  );
}

export function NmbrsSyncHistory() {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const limit = 10;

  const { data, isLoading, error } = useSyncLog(page, limit);

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 py-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2F5A7A]" />
        <span className="text-sm text-gray-500">Sync historie laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 text-[#D64545] text-sm">
        Kan sync historie niet laden: {error instanceof Error ? error.message : 'Onbekende fout'}
      </div>
    );
  }

  const logData = data?.data;
  if (!logData) return null;

  const { logs, pagination } = logData;

  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm overflow-hidden">
      <div className="p-4 border-b border-[#E2E8F0]">
        <h4 className="text-sm font-semibold text-[#2F5A7A]">
          Sync Historie ({pagination.total} totaal)
        </h4>
      </div>

      {logs.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-sm text-gray-500">Geen sync operaties gevonden.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E2E8F0]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Tijdstip
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Resultaat
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {logs.map((entry) => (
                  <SyncLogRow
                    key={entry.id}
                    entry={entry}
                    isExpanded={expandedId === entry.id}
                    onToggle={() =>
                      setExpandedId((prev) => (prev === entry.id ? null : entry.id))
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0]">
              <p className="text-sm text-gray-500">
                Pagina {pagination.page} van {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-[#E2E8F0] text-[#2F5A7A] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Vorige
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-[#E2E8F0] text-[#2F5A7A] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Volgende
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
